/**
 * WearProx Interactions - 统一品牌微交互
 * 提供 scroll-triggered 入场动画、按钮微交互、卡片 hover 增强
 * 适配 WearProx 品牌设计语言：暖白/深黑对比、cubic-bezier(0.4, 0, 0.2, 1)
 */
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

(function () {
  'use strict';

  // 仅在浏览器环境运行
  if (typeof window === 'undefined' || !document) return;

  // 跳过 reduce motion 偏好
  var prefersReduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * 将旧版自定义商品区接入 Horizon / Shopify 的标准购物车事件。
   * 抽屉、数量气泡和所有 cart-items-component 会由同一事件更新。
   */
  function refreshCartComponents(action, legacyDetail) {
    var detail = legacyDetail || {};
    var quantity = Number(detail.quantity || 1);
    var merchandiseId = detail.variant_id || detail.variantId || detail.id || '';
    var deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(new CartLinesUpdateEvent({
      action: action || 'add',
      context: 'product',
      lines: merchandiseId ? [{ merchandiseId: merchandiseId, quantity: quantity }] : [],
      promise: deferred.promise
    }));

    var cartUrl = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
    fetch(cartUrl + '.json', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to refresh cart');
        return response.json();
      })
      .then(function (cart) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            itemCount: cart.item_count,
            source: 'wearprox-cart-bridge',
            didError: false
          }
        });
      })
      .catch(function (error) {
        deferred.reject(error);
      });
  }

  function initCartEventBridge() {
    if (window.__wearproxCartBridgeReady) return;
    window.__wearproxCartBridgeReady = true;

    document.addEventListener('cart:build', function (event) {
      refreshCartComponents('add', event.detail);
    });
    document.addEventListener('cart:updated', function (event) {
      refreshCartComponents('add', event.detail);
    });
  }

  /**
   * 普通 Shopify product form 统一改为 AJAX 加购；原生 product-form-component
   * 以及已有专用 AJAX 的 XG9 / smart showcase 表单继续走各自实现。
   */
  function initNativeCartForms() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');

    forms.forEach(function (form) {
      if (form.dataset.wpxCartFormInit) return;
      if (form.closest('product-form-component')) return;
      if (form.matches('.xg9-add-to-cart-form, .wp-sps__form')) return;
      form.dataset.wpxCartFormInit = '1';

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var button = event.submitter || form.querySelector('[type="submit"]');
        if (button && button.disabled) return;

        var originalButtonHtml = button ? button.innerHTML : '';
        var formData = new FormData(form);
        var quantity = Number(formData.get('quantity') || 1);
        var variantId = formData.get('id');
        var sectionIds = [];

        document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (component) {
          if (sectionIds.indexOf(component.dataset.sectionId) === -1) {
            sectionIds.push(component.dataset.sectionId);
          }
        });
        if (sectionIds.length) formData.set('sections', sectionIds.join(','));

        if (button) {
          button.disabled = true;
          button.textContent = (window.Theme && Theme.translations && Theme.translations.adding) || 'Adding…';
        }

        var deferred = CartLinesUpdateEvent.createPromise();
        form.dispatchEvent(new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity: quantity }],
          promise: deferred.promise
        }));

        var addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
        var cartUrl = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
        var sections = null;
        var succeeded = false;

        fetch(addUrl, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
          .then(function (response) {
            return response.json().then(function (data) {
              if (!response.ok || data.status) throw data;
              return data;
            });
          })
          .then(function (response) {
            sections = response.sections || null;
            return fetch(cartUrl + '.json', {
              headers: { 'Accept': 'application/json' },
              credentials: 'same-origin'
            });
          })
          .then(function (response) {
            if (!response.ok) throw new Error('Unable to refresh cart');
            return response.json();
          })
          .then(function (cart) {
            succeeded = true;
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: {
                sections: sections,
                items: cart.items,
                itemCount: cart.item_count,
                source: 'wearprox-product-form',
                didError: false
              }
            });
            if (button) {
              button.textContent = (window.Theme && Theme.translations && Theme.translations.added) || 'Added';
            }
          })
          .catch(function (error) {
            var message = error.description || error.message || 'Could not add to cart.';
            deferred.reject(error instanceof Error ? error : new Error(message));
            form.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
            if (button) button.textContent = message;
          })
          .finally(function () {
            window.setTimeout(function () {
              if (!button) return;
              button.disabled = false;
              button.innerHTML = originalButtonHtml;
            }, succeeded ? 1100 : 1800);
          });
      });
    });
  }

  /**
   * 1. Scroll-Triggered 入场动画
   * 给带有 .wp-reveal 类的元素添加 .wp-reveal--visible 当进入视口
   */
  function initRevealOnScroll() {
    var revealEls = document.querySelectorAll('.wp-reveal:not(.wp-reveal--initialized)');
    if (!revealEls.length) return;

    revealEls.forEach(function (el) {
      el.classList.add('wp-reveal--initialized');
    });

    if (prefersReduce) {
      revealEls.forEach(function (el) { el.classList.add('wp-reveal--visible'); });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      revealEls.forEach(function (el) { el.classList.add('wp-reveal--visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          // stagger 兄弟元素
          var siblings = Array.prototype.filter.call(
            entry.target.parentElement ? entry.target.parentElement.children : [],
            function (s) { return s.classList && s.classList.contains('wp-reveal'); }
          );
          var idx = siblings.indexOf(entry.target);
          var delay = idx > 0 ? Math.min(idx * 80, 400) : 0;
          setTimeout(function () { entry.target.classList.add('wp-reveal--visible'); }, delay);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });

    revealEls.forEach(function (el) { observer.observe(el); });
  }

  /**
   * 2. 按钮微交互 - active 时 scale(0.98)
   * 给所有 .wp-cta, .xg9-hero-cta, .xg9-cta-button, .wp-pdp__add 按钮添加 active 反馈
   */
  function initButtonMicroInteractions() {
    var selectors = '.wp-cta, .xg9-hero-cta, .xg9-cta-button, .wp-pdp__add, .pd-card-link, .wp-pdp__mobile-buy button';
    document.querySelectorAll(selectors).forEach(function (btn) {
      if (btn.dataset.wpMicroInit) return;
      btn.dataset.wpMicroInit = '1';
      btn.addEventListener('pointerdown', function () {
        if (!prefersReduce) btn.style.transform = (btn.style.transform || '').replace('translateY\\(-2px\\)', '').trim();
      });
      btn.addEventListener('pointerup', function () { btn.style.transform = ''; });
      btn.addEventListener('pointerleave', function () { btn.style.transform = ''; });
    });
  }

  /**
   * 3. XG9 颜色卡片 - 点击切换变体并滚动到加购
   */
  function initXG9ColorCards() {
    var cards = document.querySelectorAll('.xg9-color-card[data-variant-id]');
    if (!cards.length) return;

    // 变体数据通过 window.__XG9_VARIANTS__ 注入
    var variants = window.__XG9_VARIANTS__ || [];
    var heroImg = document.querySelector('.xg9-hero-image img');
    var heroPrice = document.querySelector('.xg9-hero-price .price');
    var ctaBtn = document.querySelector('.xg9-cta-button');

    cards.forEach(function (card) {
      if (card.dataset.wpCardInit) return;
      card.dataset.wpCardInit = '1';

      card.addEventListener('click', function (e) {
        e.preventDefault();
        var variantId = card.getAttribute('data-variant-id');
        var variantImg = card.getAttribute('data-variant-image');
        var variantPrice = card.getAttribute('data-variant-price');
        var variantTitle = card.getAttribute('data-variant-title');

        // 选中状态切换
        cards.forEach(function (c) { c.classList.remove('xg9-color-card--active'); });
        card.classList.add('xg9-color-card--active');

        // 更新 Hero 主图
        if (heroImg && variantImg) {
          heroImg.style.opacity = '0';
          setTimeout(function () {
            heroImg.src = variantImg;
            heroImg.alt = variantTitle || 'XG9 Smart Watch';
            heroImg.style.opacity = '1';
          }, 200);
        }

        // 更新价格
        if (heroPrice && variantPrice) heroPrice.textContent = variantPrice;
        if (ctaBtn && variantPrice) {
          ctaBtn.textContent = 'Buy XG9 — ' + variantPrice + ' →';
        }

        // 更新加购表单 variant id
        var variantInput = document.querySelector('[data-variant-id-input]');
        if (variantInput) variantInput.value = variantId;

        // 更新 URL
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', '?variant=' + variantId);
        }

        // 平滑滚动到加购区
        var buySection = document.getElementById('xg9-buy');
        if (buySection) {
          buySection.scrollIntoView({ behavior: prefersReduce ? 'auto' : 'smooth', block: 'start' });
        }
      });
    });
  }

  /**
   * 4. XG9 加购表单 - AJAX 提交 + 反馈
   */
  function initXG9AddToCart() {
    var form = document.querySelector('.xg9-add-to-cart-form');
    if (!form || form.dataset.wpFormInit) return;
    form.dataset.wpFormInit = '1';

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('[type="submit"]');
      var originalText = btn ? btn.textContent : '';
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'ADDING...';
      }

      var formData = new FormData(form);
      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok || data.status) throw data;
            return data;
          });
        })
        .then(function () {
          if (btn) {
            btn.textContent = '✓ ADDED';
            btn.style.background = '#35a853';
          }
          // 触发 cart drawer 更新
          document.dispatchEvent(new CustomEvent('cart:build'));
          setTimeout(function () {
            if (btn) {
              btn.disabled = false;
              btn.textContent = originalText;
              btn.style.background = '';
            }
          }, 2000);
        })
        .catch(function () {
          if (btn) {
            btn.textContent = '✗ ERROR';
            btn.style.background = '#c00';
            setTimeout(function () {
              btn.disabled = false;
              btn.textContent = originalText;
              btn.style.background = '';
            }, 2000);
          }
        });
    });
  }

  /**
   * 5. Coming Soon 卡片点击拦截
   */
  function initComingSoonCards() {
    var comingCards = document.querySelectorAll('.pd-card-coming');
    comingCards.forEach(function (card) {
      if (card.dataset.wpComingInit) return;
      card.dataset.wpComingInit = '1';
      card.addEventListener('click', function (e) {
        e.preventDefault();
        showToast('Coming Soon — Be the first to know when we launch.');
      });
    });
  }

  /**
   * Toast 提示
   */
  function showToast(message) {
    var existing = document.querySelector('.wp-toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.className = 'wp-toast';
    toast.textContent = message;
    toast.setAttribute('role', 'status');
    toast.style.cssText = [
      'position:fixed',
      'bottom:30px',
      'left:50%',
      'transform:translateX(-50%) translateY(20px)',
      'background:#0a0a0a',
      'color:#faf8f4',
      'padding:14px 28px',
      'font-family:Inter,-apple-system,sans-serif',
      'font-size:12px',
      'font-weight:400',
      'letter-spacing:0.1em',
      'text-transform:uppercase',
      'z-index:9999',
      'opacity:0',
      'transition:all 0.4s cubic-bezier(0.4,0,0.2,1)',
      'box-shadow:0 12px 32px rgba(0,0,0,0.2)',
      'border:1px solid rgba(255,255,255,0.1)'
    ].join(';');
    document.body.appendChild(toast);

    requestAnimationFrame(function () {
      toast.style.opacity = '1';
      toast.style.transform = 'translateX(-50%) translateY(0)';
    });

    setTimeout(function () {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(-50%) translateY(20px)';
      setTimeout(function () { toast.remove(); }, 400);
    }, 3000);
  }

  /**
   * 6. WearProx Vision 产品页 - 图片点击 lightbox
   */
  function initLightbox() {
    var mediaEls = document.querySelectorAll('.wp-pdp__media[data-full-image]');
    if (!mediaEls.length) return;

    mediaEls.forEach(function (media) {
      if (media.dataset.wpLightboxInit) return;
      media.dataset.wpLightboxInit = '1';
      media.style.cursor = 'zoom-in';
      media.addEventListener('click', function () {
        var fullSrc = media.getAttribute('data-full-image');
        var overlay = document.createElement('div');
        overlay.className = 'wp-lightbox';
        overlay.style.cssText = [
          'position:fixed',
          'inset:0',
          'background:rgba(10,10,10,0.92)',
          'display:flex',
          'align-items:center',
          'justify-content:center',
          'z-index:9998',
          'cursor:zoom-out',
          'opacity:0',
          'transition:opacity 0.3s ease'
        ].join(';');
        var img = document.createElement('img');
        img.src = fullSrc;
        img.style.cssText = 'max-width:90vw;max-height:90vh;object-fit:contain;mix-blend-mode:multiply;';
        overlay.appendChild(img);
        document.body.appendChild(overlay);
        requestAnimationFrame(function () { overlay.style.opacity = '1'; });
        overlay.addEventListener('click', function () {
          overlay.style.opacity = '0';
          setTimeout(function () { overlay.remove(); }, 300);
        });
      });
    });
  }

  /**
   * 7. WearProx Vision 产品页 - Variant 切换
   */
  function initVisionVariantSwitch() {
    var variantBtns = document.querySelectorAll('.wp-pdp__variant-btn');
    if (!variantBtns.length) return;

    variantBtns.forEach(function (btn) {
      if (btn.dataset.wpVariantInit) return;
      btn.dataset.wpVariantInit = '1';

      btn.addEventListener('click', function () {
        var variantId = btn.getAttribute('data-variant-id');
        var variantPrice = btn.getAttribute('data-variant-price');
        var variantImg = btn.getAttribute('data-variant-image');

        variantBtns.forEach(function (b) { b.classList.remove('wp-pdp__variant-btn--active'); });
        btn.classList.add('wp-pdp__variant-btn--active');

        // 更新价格
        var priceEl = document.querySelector('.wp-pdp__price-value');
        if (priceEl && variantPrice) priceEl.textContent = variantPrice;

        // 更新隐藏 variant input
        var variantInput = document.querySelector('[data-variant-id]');
        if (variantInput) variantInput.value = variantId;

        // 更新主图
        if (variantImg) {
          var firstMedia = document.querySelector('.wp-pdp__media');
          if (firstMedia) {
            var img = firstMedia.querySelector('img');
            if (img) {
              img.style.opacity = '0';
              setTimeout(function () {
                img.src = variantImg;
                img.style.opacity = '1';
              }, 200);
            }
          }
        }

        // 更新 URL
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', '?variant=' + variantId);
        }
      });
    });
  }

  /**
   * 8. Sticky 购买栏 - 移动端显示/隐藏
   */
  function initStickyBuyBar() {
    var stickyBar = document.querySelector('.wp-pdp__mobile-buy');
    if (!stickyBar) return;

    var purchaseSection = document.querySelector('.wp-pdp__purchase');
    if (!purchaseSection) return;

    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          stickyBar.style.transform = 'translateY(100%)';
          stickyBar.style.opacity = '0';
        } else {
          stickyBar.style.transform = 'translateY(0)';
          stickyBar.style.opacity = '1';
        }
      });
    }, { threshold: 0 });

    observer.observe(purchaseSection);
  }

  // 初始化所有交互
  function initAll() {
    initCartEventBridge();
    initNativeCartForms();
    initRevealOnScroll();
    initButtonMicroInteractions();
    initXG9ColorCards();
    initXG9AddToCart();
    initComingSoonCards();
    initLightbox();
    initVisionVariantSwitch();
    initStickyBuyBar();
  }

  // DOM 就绪后初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // 适配 Shopify section reload（theme editor）
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:select', initAll);

  // 暴露给外部以便调试
  window.WearProxInteractions = {
    init: initAll,
    showToast: showToast
  };
})();
